import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, FileText, Tag } from "lucide-react";
import { formatCurrency, formatCurrencyWithTenant } from "@/lib/utils";
import { useTenantCurrency } from "@/hooks/use-tenant";
import { formatCurrencyWithTenantAndConversion, isCurrencyConversionAvailable } from "@/lib/currency-conversion";

interface SimpleClaimDetailsProps {
  claimIdentifier?: string;
  claimAmount: number;
  claimAmountCurrency: string;
  claimType?: string;
  claimHeadline: string;
  claimSummary?: string;
}

export default function SimpleClaimDetails({
  claimIdentifier = "Unknown",
  claimAmount,
  claimAmountCurrency,
  claimType = "Unknown",
  claimHeadline,
  claimSummary,
}: SimpleClaimDetailsProps) {
  const { currency: tenantCurrency } = useTenantCurrency();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Claim ID Card */}
      <Card className="relative">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center">
            <FileText className="h-4 w-4 mr-2 text-blue-600 flex-shrink-0" />
            Claim ID
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{claimIdentifier}</div>
        </CardContent>
      </Card>

      {/* Claim Type Card */}
      <Card className="relative">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center">
            <Tag className="h-4 w-4 mr-2 text-orange-600 flex-shrink-0" />
            Type
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{claimType}</div>
        </CardContent>
      </Card>

      {/* Submitted Amount Card */}
      <Card className="relative">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center">
            <DollarSign className="h-4 w-4 mr-2 text-green-600 flex-shrink-0" />
            Claimed Amount
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {formatCurrencyWithTenantAndConversion(claimAmount, claimAmountCurrency, tenantCurrency, {
              showSymbol: true,
              showOriginal: claimAmountCurrency !== tenantCurrency,
              showConversionRate: false
            })}
          </div>
          {claimAmountCurrency !== tenantCurrency && isCurrencyConversionAvailable(claimAmountCurrency, tenantCurrency) && (
            <div className="text-xs text-muted-foreground mt-1">
              Phase 2: Enhanced conversion available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Claim Headline & Summary Combined Card - Large (spans 2 columns) */}
      <Card className="relative lg:col-span-1">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center">
            <FileText className="h-4 w-4 mr-2 text-purple-600 flex-shrink-0" />
            {claimHeadline || "Claim Details"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            {claimSummary || "No additional details available"}
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 